import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type AuthSession,
  type AuthUser,
  type LoginPayload,
  authService,
} from '@/lib/auth';
import { identifyUser, resetIdentity } from '@/lib/analytics';
import { getToken } from '@/lib/http';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (payload: LoginPayload) => Promise<AuthSession>;
  logout: () => Promise<void>;
  /**
   * Relê a sessão no servidor. Para quando algo que vem no usuário muda por
   * ação da própria pessoa — o aceite dos termos — e a tela precisa refletir
   * sem um F5.
   */
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);

  /**
   * Único lugar que grava o usuário — e por isso o único que identifica.
   *
   * Os três caminhos que produzem sessão (restaurar do token, login, logout)
   * passam por aqui. Espalhar o `identify` pelos três deixaria o dia em que
   * surgir um quarto sem identificação nenhuma, em silêncio.
   */
  const aplicarUsuario = useCallback((proximo: AuthUser | null) => {
    setUser(proximo);
    if (proximo) identifyUser(proximo.id, proximo.role ?? null);
  }, []);
  const [isLoading, setIsLoading] = useState<boolean>(() => Boolean(getToken()));

  useEffect(() => {
    if (!getToken()) {
      setIsLoading(false);
      return;
    }
    authService
      .session()
      .then((session) => aplicarUsuario(session?.user ?? null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    const session = await authService.login(payload);
    aplicarUsuario(session.user);
    return session;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      // `finally` porque o token já foi apagado mesmo com o backend fora: a
      // sessão local acabou, e o resto precisa acabar junto.
      aplicarUsuario(null);
      // `reset` DEPOIS de zerar o usuário, e sempre: sem ele o próximo login no
      // mesmo dispositivo herda a identidade do anterior e o PostHog funde as
      // duas pessoas num funil só.
      resetIdentity();
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const session = await authService.session();
    // Sessão que sumiu no meio do caminho já é tratada pelo 401 do `http.ts`;
    // aqui só se atualiza quem continua logado.
    if (session) aplicarUsuario(session.user);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      login,
      logout,
      refreshSession,
    }),
    [user, isLoading, login, logout, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
